import { useReducer } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { TodoContextLine } from "./TodoContextLine";
import { useToast } from "@/components/ui/use-toast";
import {
  Activity,
  MAX_HIDDEN_REASON_CHARACTERS,
  useHideTodo,
} from "@/lib/api/customer";

interface HideTodoDialogProps {
  todo: Activity | null;
  onClose: () => void;
  onHidden?: (entity: Activity) => void;
}

interface DialogState {
  reason: string;
  isSubmitting: boolean;
}

type DialogAction =
  | { type: "reasonChanged"; reason: string }
  | { type: "submitStarted" }
  | { type: "submitFinished" };

const initialState: DialogState = { reason: "", isSubmitting: false };

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
  switch (action.type) {
    case "reasonChanged":
      return { ...state, reason: action.reason };
    case "submitStarted":
      return { ...state, isSubmitting: true };
    case "submitFinished":
      return { ...state, isSubmitting: false };
  }
}

// Keying on the todo id remounts the dialog, which resets the reason when a
// different todo is opened.
export function HideTodoDialog({ todo, onClose, onHidden }: HideTodoDialogProps) {
  return (
    <HideTodoDialogContent
      key={todo?.id ?? "closed"}
      todo={todo}
      onClose={onClose}
      onHidden={onHidden}
    />
  );
}

function HideTodoDialogContent({ todo, onClose, onHidden }: HideTodoDialogProps) {
  const { toast } = useToast();
  const hideTodo = useHideTodo();
  const [{ reason, isSubmitting }, dispatch] = useReducer(
    dialogReducer,
    initialState,
  );
  const isOpen = todo !== null;

  const requestClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleHide = async () => {
    if (!todo || !reason.trim() || isSubmitting) return;

    dispatch({ type: "submitStarted" });

    try {
      const result = await hideTodo({ todo_id: todo.id, hidden_reason: reason });

      if (!result.success) {
        toast({
          title: "Unable to hide Todo",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Todo hidden",
        description: "The Todo will not appear in the To-Dos list.",
      });
      onHidden?.(result.data.entity);
      onClose();
    } catch {
      toast({
        title: "Unable to hide Todo",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "submitFinished" });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) requestClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Hide Todo</DialogTitle>
          <TodoContextLine todo={todo} />
          <DialogDescription>
            Hidden Todos are removed from both the Transcriptions and To-Dos
            lists. A reason is required.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <label htmlFor="todo-hide-reason" className="text-sm font-medium">
            Hide Reason
          </label>
          <Textarea
            id="todo-hide-reason"
            data-testid="todo-hide-reason"
            value={reason}
            onChange={(event) =>
              dispatch({ type: "reasonChanged", reason: event.target.value })
            }
            maxLength={MAX_HIDDEN_REASON_CHARACTERS}
            rows={6}
            placeholder="Describe why this Todo should be hidden..."
            disabled={isSubmitting}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            {reason.length.toLocaleString()} /{" "}
            {MAX_HIDDEN_REASON_CHARACTERS.toLocaleString()} characters
          </p>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={requestClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            data-testid="todo-hide-confirm"
            onClick={handleHide}
            disabled={!reason.trim() || isSubmitting}
          >
            {isSubmitting ? "Hiding..." : "Hide Todo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
